<div class="guide-backdrop" *ngIf="isOpen" (click)="onBackdropClick()">
  <div class="guide-dialog" (click)="onDialogClick($event)">

    <!-- Mobile top bar -->
    <div class="guide-mobile-header">
      <button class="guide-menu-btn" (click)="toggleSidebar()">
        <mat-icon>menu</mat-icon>
      </button>
      <span class="guide-mobile-title">User Guide</span>
      <button class="guide-close-btn" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- Sidebar -->
    <aside class="guide-sidebar" [class.open]="sidebarOpen">
      <div class="guide-sidebar-header">
        <h2>User Guide</h2>
        <button class="guide-close-btn desktop-close" (click)="close()">
          <mat-icon>close</mat-icon>
        </button>
      </div>
      <nav class="guide-nav">
        <button
          *ngFor="let section of sections"
          class="guide-nav-item"
          [class.active]="activeSection === section.id"
          (click)="navigateTo(section.id)">
          <mat-icon class="guide-nav-icon">{{ section.icon }}</mat-icon>
          <span>{{ section.title }}</span>
        </button>
      </nav>
    </aside>

    <!-- Mobile sidebar overlay -->
    <div class="guide-sidebar-overlay" *ngIf="sidebarOpen" (click)="toggleSidebar()"></div>

    <!-- Main content area -->
    <main class="guide-content">

      <!-- ==================== WELCOME ==================== -->
      <section id="guide-section-welcome" class="guide-section">
        <h1>Welcome to MySembra</h1>
        <p>
          MySembra is your all-in-one workplace dashboard. It brings together messaging, ticket tracking,
          and all of your work apps into a single place you can access from any device with a web browser.
        </p>
        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> You can install MySembra on your phone's home screen for quick access,
            just like a regular app. The system will prompt you when this option is available.
          </div>
        </div>
        <p>This guide will walk you through every part of MySembra so you feel confident using it day to day.</p>
        <h3>What Can You Do with MySembra?</h3>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Feature</th>
              <th>What It Does</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Message Center</strong></td>
              <td>Send and receive real-time messages with coworkers and supervisors</td>
            </tr>
            <tr>
              <td><strong>Threads</strong></td>
              <td>Create and track support tickets for work-related issues</td>
            </tr>
            <tr>
              <td><strong>SembraApps</strong></td>
              <td>Access all your work applications from one place with a single sign-on</td>
            </tr>
            <tr>
              <td><strong>Profile</strong></td>
              <td>Manage your account settings and notification preferences</td>
            </tr>
            <tr>
              <td><strong>Feedback</strong></td>
              <td>Share your ideas and suggestions to help improve MySembra</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- ==================== LOGGING IN ==================== -->
      <section id="guide-section-logging-in" class="guide-section">
        <h1>Logging In</h1>
        <p>
          To use MySembra, you need to sign in with the email address or username and password
          provided by your organization.
        </p>

        <h3>How to Log In</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Open your web browser and go to your MySembra URL (for example, <strong>app.mysembra.com</strong>).
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Enter your <strong>email address</strong> or <strong>SembraTime username</strong> in the first field.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Enter your <strong>password</strong> in the second field. Your password must be at least
              8 characters long and include an uppercase letter, a lowercase letter, and a number.
            </div>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <div class="step-content">
              Click the <strong>Submit</strong> button. If your information is correct, you will be
              taken to the Home Page.
            </div>
          </div>
        </div>

        <div class="warning-box">
          <mat-icon>warning</mat-icon>
          <div>
            <strong>Important:</strong> If you enter the wrong password, you may be redirected to the
            Forgot Password page. Do not try to guess your password too many times, as your account
            may be temporarily locked.
          </div>
        </div>

        <h3>Login Page Fields</h3>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>What to Enter</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Email / Username</strong></td>
              <td>Your work email address or your SembraTime username</td>
            </tr>
            <tr>
              <td><strong>Password</strong></td>
              <td>Your account password (minimum 8 characters with mixed case and numbers)</td>
            </tr>
          </tbody>
        </table>

        <h3>Forgot Your Password?</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Click the <strong>Forgot Password</strong> link below the login form.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Enter your <strong>username or email</strong> and click <strong>Submit</strong>.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Check your email for a verification code. Enter the code and create a new password
              on the Reset Password page.
            </div>
          </div>
        </div>
      </section>

      <!-- ==================== HOME PAGE ==================== -->
      <section id="guide-section-home-page" class="guide-section">
        <h1>Home Page</h1>
        <p>
          The Home Page is your main dashboard. After logging in, this is the first screen you see.
          It gives you quick access to all MySembra features and your work apps.
        </p>

        <h3>Greeting Banner</h3>
        <p>
          At the top of the page, you will see a welcome message with a brief introduction to MySembra.
          If you prefer not to see this message, click the <strong>(Hide)</strong> link next to the greeting.
          You can turn it back on later from your Profile page.
        </p>

        <h3>MySembra Tools</h3>
        <p>These are the main tools available on the Home Page. Click any card to open that tool.</p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Tool Card</th>
              <th>What It Does</th>
              <th>Who Can See It</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Message Center</strong></td>
              <td>Opens your chat conversations so you can send and read messages</td>
              <td>Everyone</td>
            </tr>
            <tr>
              <td><strong>Threads</strong></td>
              <td>Opens the ticket tracking system where you manage support requests</td>
              <td>Everyone</td>
            </tr>
            <tr>
              <td><strong>Broadcast</strong></td>
              <td>Send a message to many people at once</td>
              <td>Admins and supervisors only</td>
            </tr>
          </tbody>
        </table>

        <h3>SembraApps</h3>
        <p>
          Below the MySembra Tools section, you will find your <strong>SembraApps</strong>. These are
          links to other work applications set up by your organization. Clicking an app card opens it
          in a new browser tab, and you are automatically signed in.
        </p>

        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> If you don't see an app you expect, contact your administrator.
            They control which apps appear on the Home Page.
          </div>
        </div>
      </section>

      <!-- ==================== NAVIGATION ==================== -->
      <section id="guide-section-navigation" class="guide-section">
        <h1>Navigation</h1>
        <p>
          MySembra has a top navigation bar (header) that stays visible on every page.
          The layout changes slightly depending on whether you are using a computer or a mobile device.
        </p>

        <h3>Desktop Navigation (Computer)</h3>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>What It Does</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>MySembra Logo</strong></td>
              <td>Click to return to the Home Page from any screen</td>
            </tr>
            <tr>
              <td><strong>Bell Icon</strong></td>
              <td>Shows your notifications. A red icon means you have unread notifications</td>
            </tr>
            <tr>
              <td><strong>Agency Selector</strong></td>
              <td>If you belong to more than one agency, use this dropdown to switch between them</td>
            </tr>
            <tr>
              <td><strong>Menu (hamburger icon)</strong></td>
              <td>Opens a dropdown with links to Home, Profile, Feedback, Help, and Logout</td>
            </tr>
          </tbody>
        </table>

        <h3>Mobile Navigation (Phone or Tablet)</h3>
        <p>
          On mobile devices, the navigation moves to a <strong>bottom bar</strong> with icon buttons.
          A welcome greeting appears at the top of the screen.
        </p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Icon</th>
              <th>What It Does</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Home</strong></td>
              <td>Returns you to the Home Page</td>
            </tr>
            <tr>
              <td><strong>Logout</strong></td>
              <td>Signs you out of MySembra</td>
            </tr>
          </tbody>
        </table>

        <h3>Chat Bubble</h3>
        <p>
          On every page, you will see a floating <strong>chat bubble</strong> in the bottom-right corner.
          The number on the bubble shows how many unread messages you have. Click it to open your
          most recent conversation.
        </p>

        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> Your session will automatically end after 12 hours of
            no activity. You will see a warning 60 seconds before being logged out.
          </div>
        </div>
      </section>

      <!-- ==================== MESSAGE CENTER ==================== -->
      <section id="guide-section-message-center" class="guide-section">
        <h1>Message Center</h1>
        <p>
          The Message Center is where all your conversations live. You can send text messages,
          share files, and forward messages to other people.
        </p>

        <h3>Viewing Your Conversations</h3>
        <p>
          When you open the Message Center (from the Home Page card or the chat bubble), you see a
          list of all your conversations. This list is called <strong>Connections</strong>.
        </p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Column / Element</th>
              <th>What It Means</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Name</strong></td>
              <td>The person or group you are chatting with</td>
            </tr>
            <tr>
              <td><strong>Blue dot</strong></td>
              <td>You have unread messages in this conversation</td>
            </tr>
            <tr>
              <td><strong>Count in parentheses</strong></td>
              <td>Number of unread messages (e.g., "(3)" means 3 unread)</td>
            </tr>
            <tr>
              <td><strong>Preview text</strong></td>
              <td>A short preview of the last message sent</td>
            </tr>
            <tr>
              <td><strong>Time</strong></td>
              <td>When the last message was sent or received</td>
            </tr>
          </tbody>
        </table>

        <h3>Sending a Message</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Click on a conversation from your Connections list, or start a new one (see below).
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Type your message in the text box at the bottom of the chat window.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Press <strong>Enter</strong> on your keyboard or click the <strong>send icon</strong>
              (paper airplane) to send.
            </div>
          </div>
        </div>

        <h3>Attaching a File</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Click the <strong>paperclip icon</strong> next to the message text box.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Select a file from your device. The file will be uploaded and sent automatically.
            </div>
          </div>
        </div>

        <h3>Forwarding a Message</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Check the <strong>checkbox</strong> next to one or more messages you want to forward.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Click the <strong>Forward Message</strong> button that appears above the text box.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Choose the person you want to forward the messages to.
            </div>
          </div>
        </div>

        <h3>Creating a Ticket from a Chat</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Select one or more messages using the checkboxes.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Click the <strong>Create Ticket</strong> button. This starts a new support ticket
              with the selected messages as context.
            </div>
          </div>
        </div>

        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> Message status icons appear next to your sent messages.
            A single check means sent, a double check means delivered, and a colored double
            check means the message has been read.
          </div>
        </div>

        <h3>Starting a New Conversation</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              In the Connections view, click the <strong>menu icon</strong> (three dots) in the top-right corner.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Select <strong>New Message</strong> from the dropdown menu.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Search for the person you want to message, then click their name to begin chatting.
            </div>
          </div>
        </div>

        <h3>Searching Conversations</h3>
        <p>
          Use the search bar at the top of the Connections list to filter by <strong>username</strong>
          or <strong>message content</strong>. Results update as you type.
        </p>
      </section>

      <!-- ==================== ADDRESS BOOK ==================== -->
      <section id="guide-section-address-book" class="guide-section">
        <h1>Address Book</h1>
        <p>
          The Address Book shows all users in your organization. You can search for people and
          start a direct conversation with them.
        </p>

        <h3>How to Use the Address Book</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              In the Message Center, click the <strong>menu icon</strong> and select <strong>Address Book</strong>.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Use the search bar to find someone by <strong>name, IVRID, role, agency, or branch</strong>.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Click on a person's name to open a chat conversation with them.
            </div>
          </div>
        </div>

        <h3>Address Book Fields</h3>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>What It Shows</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Name</strong></td>
              <td>The person's display name</td>
            </tr>
            <tr>
              <td><strong>IVRID</strong></td>
              <td>A unique identifier number for the person (if available)</td>
            </tr>
            <tr>
              <td><strong>Role</strong></td>
              <td>Their job role (e.g., Caregiver, Agency Admin, Nurse)</td>
            </tr>
            <tr>
              <td><strong>Agency Code</strong></td>
              <td>Which agency or organization they belong to</td>
            </tr>
            <tr>
              <td><strong>Branch</strong></td>
              <td>Which branch office they work at (if applicable)</td>
            </tr>
            <tr>
              <td><strong>Tags</strong></td>
              <td>Additional labels or keywords associated with the person</td>
            </tr>
          </tbody>
        </table>

        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> If you see <span style="color: red;">"You"</span> in red next to a name,
            that entry is your own account.
          </div>
        </div>
      </section>

      <!-- ==================== THREADS ==================== -->
      <section id="guide-section-threads" class="guide-section">
        <h1>Threads (Tickets)</h1>
        <p>
          Threads is MySembra's ticket tracking system. A "ticket" (also called a "thread") is a
          record of a work issue, question, or request that needs to be tracked and resolved.
        </p>

        <h3>Threads Home</h3>
        <p>When you open Threads from the Home Page, you see the Threads Home screen with these options:</p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Button</th>
              <th>What It Shows</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>New</strong></td>
              <td>Create a brand-new ticket (only visible if you have permission)</td>
            </tr>
            <tr>
              <td><strong>Search All Threads</strong></td>
              <td>Search across all tickets using filters</td>
            </tr>
            <tr>
              <td><strong>Assigned to Me</strong></td>
              <td>Tickets where you are listed as the assigned (primary) user</td>
            </tr>
            <tr>
              <td><strong>Unclaimed</strong></td>
              <td>Tickets that have not been assigned to anyone yet</td>
            </tr>
            <tr>
              <td><strong>Watching</strong></td>
              <td>Tickets you are watching but are not the primary person assigned</td>
            </tr>
            <tr>
              <td><strong>Closed</strong></td>
              <td>Tickets that have been completed and closed</td>
            </tr>
          </tbody>
        </table>

        <h3>Viewing Ticket Lists</h3>
        <p>
          When you click any of the buttons above, you see a list of matching tickets. On a computer,
          this appears as a table. On a phone, each ticket is displayed as a card.
        </p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Column</th>
              <th>What It Means</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Title</strong></td>
              <td>A short name describing the issue (click to open the ticket)</td>
            </tr>
            <tr>
              <td><strong>Created At</strong></td>
              <td>The date and time the ticket was first created</td>
            </tr>
            <tr>
              <td><strong>Description</strong></td>
              <td>A longer explanation of the issue</td>
            </tr>
            <tr>
              <td><strong>Tags</strong></td>
              <td>Labels that help categorize the ticket</td>
            </tr>
            <tr>
              <td><strong>Priority (! icon)</strong></td>
              <td>How urgent the ticket is. An exclamation icon means "Urgent"</td>
            </tr>
            <tr>
              <td><strong>Category</strong></td>
              <td>The type of issue (e.g., appointment, prescription)</td>
            </tr>
            <tr>
              <td><strong>Agency</strong></td>
              <td>Which agency the ticket belongs to</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>The current state of the ticket (e.g., Open, In Progress, Closed)</td>
            </tr>
            <tr>
              <td><strong>Due Date</strong></td>
              <td>When the ticket needs to be resolved by (if set)</td>
            </tr>
            <tr>
              <td><strong>Assigned User</strong></td>
              <td>The person responsible for handling this ticket</td>
            </tr>
          </tbody>
        </table>

        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> You can click any column header to sort the list by that column.
            Click again to reverse the sort order. On mobile, use the sort buttons at the top of the card list.
          </div>
        </div>

        <h3>Searching and Filtering Tickets</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Click <strong>Search All Threads</strong> from the Threads Home screen.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Type a keyword into the <strong>search bar</strong> to search by title, description, or tags.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Use the <strong>filter dropdowns</strong> to narrow results by priority, category, or status.
            </div>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <div class="step-content">
              Click <strong>Search</strong> to apply your filters. Click <strong>Reset</strong> to clear all filters.
            </div>
          </div>
        </div>

        <h3>Creating a New Ticket</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Click the <strong>New</strong> button on the Threads Home screen.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Fill in the <strong>Title</strong> (required, up to 50 characters) &mdash; a short name for the issue.
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Fill in the <strong>Description</strong> (required, up to 2000 characters) &mdash; explain the issue in detail.
            </div>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <div class="step-content">
              Select a <strong>Priority</strong> level (required) &mdash; how urgent this issue is.
            </div>
          </div>
          <div class="step">
            <div class="step-number">5</div>
            <div class="step-content">
              Select a <strong>Category</strong> (required) &mdash; what type of issue this is.
            </div>
          </div>
          <div class="step">
            <div class="step-number">6</div>
            <div class="step-content">
              Optionally set a <strong>Due Date</strong>, add <strong>Tags</strong>, attach files, or add watchers.
            </div>
          </div>
          <div class="step">
            <div class="step-number">7</div>
            <div class="step-content">
              Click <strong>Submit</strong> to create the ticket.
            </div>
          </div>
        </div>

        <h3>Create Ticket Form Fields</h3>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Field</th>
              <th>Required?</th>
              <th>What to Enter</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Title</strong></td>
              <td>Yes</td>
              <td>A short name for the issue (max 50 characters)</td>
            </tr>
            <tr>
              <td><strong>Description</strong></td>
              <td>Yes</td>
              <td>A detailed explanation of the issue (max 2000 characters)</td>
            </tr>
            <tr>
              <td><strong>Priority</strong></td>
              <td>Yes</td>
              <td>Select from the dropdown (e.g., Low, Medium, High, Urgent)</td>
            </tr>
            <tr>
              <td><strong>Category</strong></td>
              <td>Yes</td>
              <td>Select the type of issue from the dropdown</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>Yes (edit only)</td>
              <td>Shown only when editing an existing ticket</td>
            </tr>
            <tr>
              <td><strong>Agency Code</strong></td>
              <td>Yes (admins)</td>
              <td>Which agency this ticket is for (auto-filled for agency users)</td>
            </tr>
            <tr>
              <td><strong>Due Date</strong></td>
              <td>No</td>
              <td>Pick a date by when the issue should be resolved</td>
            </tr>
            <tr>
              <td><strong>Tags</strong></td>
              <td>No</td>
              <td>Add keyword labels to help organize and find the ticket later</td>
            </tr>
            <tr>
              <td><strong>Users Watching</strong></td>
              <td>No</td>
              <td>Add people who should be notified about updates to this ticket</td>
            </tr>
            <tr>
              <td><strong>Attachments</strong></td>
              <td>No</td>
              <td>Upload files related to the issue</td>
            </tr>
            <tr>
              <td><strong>Set As Private</strong></td>
              <td>No</td>
              <td>If turned on, only users added to the ticket can view it</td>
            </tr>
          </tbody>
        </table>

        <h3>Ticket Comments and History</h3>
        <p>
          When viewing an existing ticket, you will see two tabs at the bottom:
        </p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Tab</th>
              <th>What It Shows</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Comments</strong></td>
              <td>Notes and updates added by users. Click the "add comment" icon to add your own.</td>
            </tr>
            <tr>
              <td><strong>History (clock icon)</strong></td>
              <td>An automatic log of all changes made to the ticket (who changed what and when).</td>
            </tr>
          </tbody>
        </table>

        <div class="warning-box">
          <mat-icon>warning</mat-icon>
          <div>
            <strong>Important:</strong> Once a ticket is set to "Private," only users who are added
            to the ticket can view it. Make sure to add all necessary people before enabling this setting.
          </div>
        </div>
      </section>

      <!-- ==================== PROFILE ==================== -->
      <section id="guide-section-profile" class="guide-section">
        <h1>Profile</h1>
        <p>
          The Profile page shows your account information and lets you manage your notification
          settings and profile picture.
        </p>

        <h3>What You See on the Profile Page</h3>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Item</th>
              <th>What It Shows</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Profile Picture</strong></td>
              <td>Your current profile photo (if uploaded)</td>
            </tr>
            <tr>
              <td><strong>Name</strong></td>
              <td>Your display name in the system</td>
            </tr>
            <tr>
              <td><strong>Email</strong></td>
              <td>Your registered email address</td>
            </tr>
            <tr>
              <td><strong>Account Created</strong></td>
              <td>The date your account was set up</td>
            </tr>
          </tbody>
        </table>

        <h3>Profile Actions</h3>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Button</th>
              <th>What It Does</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Home</strong></td>
              <td>Returns you to the Home Page</td>
            </tr>
            <tr>
              <td><strong>Profile Pic Upload</strong></td>
              <td>Opens a screen where you can upload or change your profile picture</td>
            </tr>
            <tr>
              <td><strong>Notification Check</strong></td>
              <td>Sends a test notification to make sure notifications are working on your device</td>
            </tr>
            <tr>
              <td><strong>Popup Check</strong></td>
              <td>Opens a test popup to confirm popups are working</td>
            </tr>
          </tbody>
        </table>

        <h3>Notification Settings</h3>
        <p>Below your profile information, you will find toggle switches to control your notifications:</p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Setting</th>
              <th>What It Controls</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Chat Notifications</strong></td>
              <td>Get notified when someone sends you a chat message</td>
            </tr>
            <tr>
              <td><strong>Ticket Notifications - Added to a Ticket</strong></td>
              <td>Get notified when you are added as a watcher on a ticket</td>
            </tr>
            <tr>
              <td><strong>Ticket Notifications - Ticket Updates</strong></td>
              <td>Get notified when a ticket you are watching is updated</td>
            </tr>
            <tr>
              <td><strong>Silent Notifications (Turn off Sound)</strong></td>
              <td>Receive notifications without any sound</td>
            </tr>
            <tr>
              <td><strong>Hide Greeting</strong></td>
              <td>Hides the welcome message on the Home Page</td>
            </tr>
          </tbody>
        </table>

        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> Changes to notification settings are saved automatically
            when you flip a toggle. There is no separate "Save" button needed.
          </div>
        </div>
      </section>

      <!-- ==================== FEEDBACK ==================== -->
      <section id="guide-section-feedback" class="guide-section">
        <h1>Feedback</h1>
        <p>
          The Feedback page lets you share your thoughts, ideas, or suggestions about MySembra
          with the development team.
        </p>

        <h3>How to Submit Feedback</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              Go to the Feedback page from the menu or from the link on the Home Page greeting.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              Type your feedback or suggestion in the text box (up to 999 characters).
            </div>
          </div>
          <div class="step">
            <div class="step-number">3</div>
            <div class="step-content">
              Click the <strong>Submit</strong> button.
            </div>
          </div>
          <div class="step">
            <div class="step-number">4</div>
            <div class="step-content">
              You will see a "Thank you" confirmation message. Click <strong>Home</strong> to return to
              the Home Page, or submit another piece of feedback.
            </div>
          </div>
        </div>

        <div class="info-box">
          <mat-icon>lightbulb</mat-icon>
          <div>
            <strong>Tip:</strong> If you have multiple pieces of feedback, submit them one at a time
            so each one can be reviewed separately.
          </div>
        </div>
      </section>

      <!-- ==================== NOTIFICATIONS ==================== -->
      <section id="guide-section-notifications" class="guide-section">
        <h1>Notifications</h1>
        <p>
          MySembra can send you push notifications to your device so you never miss an important
          message or ticket update, even when you are not actively using the app.
        </p>

        <h3>Enabling Notifications</h3>
        <div class="steps">
          <div class="step">
            <div class="step-number">1</div>
            <div class="step-content">
              When you first log in, your browser may ask for permission to show notifications.
              Click <strong>Allow</strong> to enable them.
            </div>
          </div>
          <div class="step">
            <div class="step-number">2</div>
            <div class="step-content">
              If you missed the prompt, go to your <strong>Profile</strong> page and click
              <strong>Notification Check</strong> to trigger it again.
            </div>
          </div>
        </div>

        <h3>Notification Bell</h3>
        <p>
          The bell icon in the top navigation bar gives you quick access to your notifications panel.
          A <strong style="color: #f44336;">red bell</strong> means you have unread notifications.
          Click the bell to open the notification panel and view them.
        </p>

        <div class="warning-box">
          <mat-icon>warning</mat-icon>
          <div>
            <strong>Important:</strong> If notifications are not working, check your browser settings
            to make sure notifications are allowed for the MySembra website. On mobile devices,
            also check your device notification settings.
          </div>
        </div>
      </section>

      <!-- ==================== GLOSSARY ==================== -->
      <section id="guide-section-glossary" class="guide-section">
        <h1>Glossary</h1>
        <p>Here are definitions of terms used throughout MySembra:</p>
        <table class="guide-table">
          <thead>
            <tr>
              <th>Term</th>
              <th>Definition</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Agency</strong></td>
              <td>The organization or company you work for. Some users belong to more than one agency.</td>
            </tr>
            <tr>
              <td><strong>Agency Code</strong></td>
              <td>A short code that identifies your agency (e.g., "ACME"). Used throughout MySembra to link data to the right organization.</td>
            </tr>
            <tr>
              <td><strong>Branch</strong></td>
              <td>A physical office location within an agency.</td>
            </tr>
            <tr>
              <td><strong>Broadcast</strong></td>
              <td>A message sent to many people at once. Only administrators can send broadcasts.</td>
            </tr>
            <tr>
              <td><strong>Caregiver</strong></td>
              <td>A user role for front-line care workers. Caregivers have basic access to messaging.</td>
            </tr>
            <tr>
              <td><strong>Category</strong></td>
              <td>A label on a ticket that describes what type of issue it is (e.g., appointment, prescription).</td>
            </tr>
            <tr>
              <td><strong>Connections</strong></td>
              <td>Your list of active chat conversations in the Message Center.</td>
            </tr>
            <tr>
              <td><strong>Dashboard</strong></td>
              <td>Another name for the Home Page, which gives you an overview of your tools and apps.</td>
            </tr>
            <tr>
              <td><strong>IVRID</strong></td>
              <td>A unique identification number assigned to each user, used for system reference.</td>
            </tr>
            <tr>
              <td><strong>Notification Preferences</strong></td>
              <td>Settings on your Profile page that control which types of alerts you receive.</td>
            </tr>
            <tr>
              <td><strong>Priority</strong></td>
              <td>How urgent a ticket is. Common levels are Low, Medium, High, and Urgent.</td>
            </tr>
            <tr>
              <td><strong>Push Notification</strong></td>
              <td>An alert that pops up on your device, even when you are not looking at MySembra.</td>
            </tr>
            <tr>
              <td><strong>Realm</strong></td>
              <td>The MySembra system area your account belongs to (typically "Sembracare").</td>
            </tr>
            <tr>
              <td><strong>Role</strong></td>
              <td>Your permission level in MySembra, which determines what features you can access (e.g., Admin, Nurse, Caregiver).</td>
            </tr>
            <tr>
              <td><strong>SembraApps</strong></td>
              <td>External work applications linked on your Home Page that you can open with a single click.</td>
            </tr>
            <tr>
              <td><strong>Session</strong></td>
              <td>The time between when you log in and when you log out or get timed out.</td>
            </tr>
            <tr>
              <td><strong>Status</strong></td>
              <td>The current state of a ticket (e.g., Open, In Progress, Closed).</td>
            </tr>
            <tr>
              <td><strong>Tags</strong></td>
              <td>Keyword labels you can add to tickets or users to make them easier to find and organize.</td>
            </tr>
            <tr>
              <td><strong>Thread</strong></td>
              <td>Another name for a ticket &mdash; a tracked work issue or request.</td>
            </tr>
            <tr>
              <td><strong>Ticket</strong></td>
              <td>A record of a work issue, question, or request that is tracked from creation to resolution.</td>
            </tr>
            <tr>
              <td><strong>Watcher</strong></td>
              <td>A person added to a ticket who receives updates but is not the primary person assigned to resolve it.</td>
            </tr>
          </tbody>
        </table>
      </section>

      <!-- ==================== FAQ ==================== -->
      <section id="guide-section-faq" class="guide-section">
        <h1>Frequently Asked Questions (FAQ)</h1>

        <div class="faq-item">
          <h3>I forgot my password. What do I do?</h3>
          <p>
            Click the <strong>Forgot Password</strong> link on the login page. Enter your username or
            email, and a verification code will be sent to your email. Use that code to set a new password.
          </p>
        </div>

        <div class="faq-item">
          <h3>I'm not receiving notifications. How do I fix this?</h3>
          <div class="steps">
            <div class="step">
              <div class="step-number">1</div>
              <div class="step-content">
                Go to your <strong>Profile</strong> page and make sure the notification toggles are turned on.
              </div>
            </div>
            <div class="step">
              <div class="step-number">2</div>
              <div class="step-content">
                Click <strong>Notification Check</strong> to test if notifications work.
              </div>
            </div>
            <div class="step">
              <div class="step-number">3</div>
              <div class="step-content">
                Check your browser settings to ensure notifications are allowed for the MySembra website.
              </div>
            </div>
            <div class="step">
              <div class="step-number">4</div>
              <div class="step-content">
                On mobile, check your device's notification settings for your browser app.
              </div>
            </div>
          </div>
        </div>

        <div class="faq-item">
          <h3>I got logged out unexpectedly. Why?</h3>
          <p>
            MySembra automatically logs you out after <strong>12 hours</strong> of no activity to keep
            your account secure. You will see a 60-second warning before it happens. Simply log back in
            to continue where you left off.
          </p>
        </div>

        <div class="faq-item">
          <h3>If I work for multiple agencies, how do I switch between agencies?</h3>
          <p>
            If you belong to more than one agency, you will see a <strong>dropdown selector</strong> in
            the top navigation bar. Click it and choose the agency you want to work with. The page will
            refresh to show data for the selected agency.
          </p>
        </div>

        <div class="faq-item">
          <h3>I can't find the Broadcast tool. Where is it?</h3>
          <p>
            These tools are only visible to users with specific roles. <strong>Broadcast</strong> is
            available to administrators and supervisors.
          </p>
        </div>

        <div class="faq-item">
          <h3>How do I send a file to someone in a chat?</h3>
          <p>
            In the chat window, click the <strong>paperclip icon</strong> next to the message box.
            Choose a file from your device, and it will be uploaded and sent automatically.
          </p>
        </div>

        <div class="faq-item">
          <h3>Can I create a ticket from a chat conversation?</h3>
          <p>
            Yes! Select one or more messages by checking the boxes next to them, then click the
            <strong>Create Ticket</strong> button that appears. The selected messages will be used
            as context for the new ticket.
          </p>
        </div>

        <div class="faq-item">
          <h3>What does the "Private" toggle on a ticket do?</h3>
          <p>
            When you set a ticket as Private, only users who are specifically added to that ticket
            can see it. Other users, even admins, will not be able to find or view it unless they
            are added as watchers.
          </p>
        </div>

        <div class="faq-item">
          <h3>How do I install MySembra on my phone?</h3>
          <p>
            When you visit MySembra on your phone's browser, a banner may appear asking you to add
            it to your home screen. On <strong>Android</strong>, tap the banner. On <strong>iPhone</strong>,
            tap the Share button (square with an arrow), then tap <strong>"Add to Home Screen."</strong>
          </p>
        </div>

        <div class="faq-item">
          <h3>I see "A new version of this app is available." What should I do?</h3>
          <p>
            Click the <strong>"Update now"</strong> link in the banner. This will refresh the app to the
            latest version. It is important to update to get the newest features and fixes.
          </p>
        </div>

        <div class="faq-item">
          <h3>Who do I contact if I need help?</h3>
          <p>
            Use the <strong>Message Center</strong> to contact your administrator, or submit a
            <strong>Feedback</strong> request from the Feedback page. You can also create a
            <strong>Threads ticket</strong> for tracked support.
          </p>
        </div>
      </section>
    </main>
  </div>
</div>
